import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../auth/AuthContext";
import { getUserAiSettings, saveUserAiSettings } from "../../api/aiSettingsApi";

export default function useAiSettings() {
  const { token } = useAuth();

  const [useAi, setUseAi] = useState(false);
  // newKey: what the user has typed in the key field this session (empty = no change)
  const [newKey, setNewKey] = useState("");
  const [clearKey, setClearKey] = useState(false);
  const [model, setModel] = useState("");
  // server-state indicators (read-only, from server)
  const [hasKey, setHasKey] = useState(false);
  const [orgHasKey, setOrgHasKey] = useState(false);
  const [orgModel, setOrgModel] = useState("");

  const [loading, setLoading] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await getUserAiSettings(token);
      setUseAi(data.useAi ?? false);
      setHasKey(data.hasKey ?? false);
      setModel(data.model ?? "");
      setOrgHasKey(data.orgHasKey ?? false);
      setOrgModel(data.orgModel ?? "");
      // Sync useAi flag to localStorage so SearchContext can read it without extra fetches
      try {
        localStorage.setItem("use_server_llm", data.useAi ? "true" : "false");
      } catch {}
    } catch {
      // Fall back to localStorage if server unreachable
      try {
        const v = localStorage.getItem("use_server_llm");
        setUseAi(v === "true" || v === "1");
      } catch {}
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!token) return;
    setError("");
    setLoading(true);
    try {
      const data = await saveUserAiSettings(token, {
        useAi,
        apiKey: clearKey ? "" : (newKey || null),
        clearKey,
        model,
      });
      setHasKey(data.hasKey ?? false);
      setOrgHasKey(data.orgHasKey ?? false);
      setOrgModel(data.orgModel ?? "");
      setNewKey("");
      setClearKey(false);
      try {
        localStorage.setItem("use_server_llm", data.useAi ? "true" : "false");
      } catch {}
      setSavedFlag(true);
      setTimeout(() => setSavedFlag(false), 1500);
    } catch (e) {
      setError(e.message || "Failed to save settings.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setUseAi(false);
    setNewKey("");
    setClearKey(false);
    setModel("");
    setError("");
  };

  return {
    useAi, setUseAi,
    newKey, setNewKey,
    clearKey, setClearKey,
    model, setModel,
    hasKey,
    orgHasKey,
    orgModel,
    loading,
    savedFlag,
    error,
    save,
    reset,
  };
}
